import React, { useState, useMemo } from 'react';
import {
  Box,
  Checkbox,
  FormControlLabel,
  TextField,
  Button,
  Typography,
  Stack,
  IconButton
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import StorageFactory from '../../../Util/StorageFactory';
import { SESSION_USER_COUNTRY_DATA_KEY } from '../../../Util/VMConstants';
import { formatAmount, parsePct } from '../../../Util/UtilHelper';
import { VM_Colors } from '../../../Styles/VMStyles';

function InvoiceTax({
  taxEnabled,
  setTaxEnabled,
  taxList = [],
  setTaxList,
  totalAmount = 0,   // ✅ ORIGINAL AMOUNT ONLY
}) {
  const [newTaxName, setNewTaxName] = useState('');
  const [newTaxPercentage, setNewTaxPercentage] = useState('');
  const [editingIndex, setEditingIndex] = useState(null);
  const [editTaxName, setEditTaxName] = useState('');
  const [editTaxPercentage, setEditTaxPercentage] = useState('');

  const sessionFactory = new StorageFactory('session');
  const currencySymbol =
    sessionFactory.get(SESSION_USER_COUNTRY_DATA_KEY)?.currency_symbol || '₹';

  /* ---------------- VALIDATION ---------------- */

  const isPctValid = (v) => {
    const n = parsePct(v);
    return Number.isFinite(n) && n >= 0 && n <= 100;
  };

  const pctErrorText = (v) => {
    if (v === '' || v === null || v === undefined) return '';
    const n = parsePct(v);
    if (!Number.isFinite(n)) return 'Enter a valid number';
    if (n < 0) return 'Cannot be negative';
    if (n > 100) return 'Maximum 100%';
    return '';
  };

  /* ---------------- ADD ---------------- */

  const handleAddTax = () => {
    const percentage = parsePct(newTaxPercentage);
    if (!newTaxName.trim() || !isPctValid(percentage)) return;

    setTaxList(prev => [
      ...(Array.isArray(prev) ? prev : []),
      { name: newTaxName.trim(), percentage }
    ]);

    setNewTaxName('');
    setNewTaxPercentage('');
  };

  /* ---------------- EDIT ---------------- */

  const handleEditClick = (index, tax) => {
    setEditingIndex(index);
    setEditTaxName(tax.name);
    setEditTaxPercentage(tax.percentage);
  };

  const handleCancelEdit = () => {
    setEditingIndex(null);
    setEditTaxName('');
    setEditTaxPercentage('');
  };

  const handleSaveEdit = (index) => {
    const percentage = parsePct(editTaxPercentage);
    if (!editTaxName.trim() || !isPctValid(percentage)) return;

    const updated = [...taxList];
    updated[index] = { name: editTaxName.trim(), percentage };
    setTaxList(updated);
    handleCancelEdit();
  };

  /* ---------------- DELETE ---------------- */

  const handleDelete = (index) => {
    const updated = [...taxList];
    updated.splice(index, 1);
    setTaxList(updated);

    if (editingIndex === index) handleCancelEdit();
    else if (editingIndex > index) setEditingIndex(i => i - 1);
  };

  /* ---------------- INPUT SAFETY ---------------- */

  const onWheelPrevent = (e) => {
    // Prevent default scroll behavior and gently blur the input to avoid value changes
    e.preventDefault();
    e.stopPropagation();
    try { e.target && e.target.blur && e.target.blur(); } catch (err) {}
  };
  const onPctKeyDown = (e) => {
    // Prevent invalid numeric characters and keyboard increment/decrement
    if (['-', 'e', 'E', 'ArrowUp', 'ArrowDown'].includes(e.key)) e.preventDefault();
  };

  /* ---------------- TAX CALCULATION ---------------- */

  const taxAmounts = useMemo(() => {
    const base = Number(totalAmount) || 0;
    return taxList.map(t => {
      const pct = parsePct(t.percentage);
      return (base * pct) / 100;
    });
  }, [taxList, totalAmount]);

  /* ---------------- STYLES ---------------- */

  const cardSX = {
    background: '#fff',
    borderRadius: 2,
    boxShadow: '0 1px 4px rgba(16,24,40,0.06)',
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
    boxSizing: 'border-box',
  };

  const rowSX = {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 2,
    mb: 1.2,
    p: 1,
    borderRadius: 1,
    background: VM_Colors.white_gradient,
    justifyContent: 'space-between',
    minWidth: 0,
    width: '100%',
    maxWidth: '100%',
  };

  /* ---------------- UI ---------------- */

  return (
    <Box sx={{ display: 'flex', width: '100%', maxWidth: '100%', minWidth: 0 }}>
      <Box sx={cardSX}>
        <FormControlLabel
          control={
            <Checkbox
              checked={taxEnabled}
              onChange={(e) => setTaxEnabled(e.target.checked)}
            />
          }
          label={<Typography fontWeight={600}>Apply Taxes (e.g. GST)</Typography>}
        />

        {taxEnabled && (
          <>
            {/* ADD */}
            <Typography fontWeight={700} mt={1}>Add New Tax</Typography>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} mt={1}>
              <TextField
                label="Tax Name"
                size="small"
                value={newTaxName}
                onChange={(e) => setNewTaxName(e.target.value)}
              />

              <TextField
                size="small"
                type="number"
                label="Percentage (%)"
                value={newTaxPercentage}
                onChange={(e) => setNewTaxPercentage(e.target.value)}
                onWheel={onWheelPrevent}
                onKeyDown={onPctKeyDown}
                inputProps={{ min: 0, max: 100, inputMode: 'numeric', step: 'any' }}
                error={!!pctErrorText(newTaxPercentage)}
                helperText={pctErrorText(newTaxPercentage)}
                sx={{
                  '& input[type=number]': { MozAppearance: 'textfield' },
                  '& input[type=number]::-webkit-outer-spin-button': { WebkitAppearance: 'none', margin: 0 },
                  '& input[type=number]::-webkit-inner-spin-button': { WebkitAppearance: 'none', margin: 0 },
                }}
              />

              <Button
                variant="contained"
                onClick={handleAddTax}
                disabled={!newTaxName.trim() || !isPctValid(newTaxPercentage)}
              >
                Add
              </Button>
            </Stack>

            {/* LIST */}
            {taxList.length > 0 && (
            <Typography fontWeight={700} mt={4}>Current Taxes</Typography>
            )}
            
            {taxList.map((tax, idx) => (
              <Box key={idx} sx={rowSX}>
                {editingIndex === idx ? (
                  <>
                    <TextField
                      size="small"
                      value={editTaxName}
                      onChange={(e) => setEditTaxName(e.target.value)}
                    />

                    <TextField
                      size="small"
                      type="number"
                      value={editTaxPercentage}
                      onChange={(e) => setEditTaxPercentage(e.target.value)}
                      onWheel={onWheelPrevent}
                      onKeyDown={onPctKeyDown}
                      inputProps={{ min: 0, max: 100, inputMode: 'numeric', step: 'any' }}
                      sx={{
                        '& input[type=number]': { MozAppearance: 'textfield' },
                        '& input[type=number]::-webkit-outer-spin-button': { WebkitAppearance: 'none', margin: 0 },
                        '& input[type=number]::-webkit-inner-spin-button': { WebkitAppearance: 'none', margin: 0 },
                      }}
                    />

                    <Typography fontWeight={600}>
                      {formatAmount((totalAmount * parsePct(editTaxPercentage)) / 100, currencySymbol)}
                    </Typography>

                    <Stack direction="row" spacing={1}>
                      <Button size="small" variant="contained" onClick={() => handleSaveEdit(idx)}>
                        Apply
                      </Button>
                      <Button size="small" variant="outlined" onClick={handleCancelEdit}>
                        Cancel
                      </Button>
                    </Stack>
                  </>
                ) : (
                  <>
                    <Typography fontWeight={600}>
                      {tax.name} ({tax.percentage}%)
                    </Typography>

                    <Typography fontWeight={700} color="primary.main">
                      {formatAmount(taxAmounts[idx], currencySymbol)}
                    </Typography>

                    <Stack direction="row" spacing={1}>
                      <Button size="small" variant="outlined" onClick={() => handleEditClick(idx, tax)}>
                        Edit
                      </Button>
                      <IconButton size="small" onClick={() => handleDelete(idx)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  </>
                )}
              </Box>
            ))}
          </>
        )}
      </Box>
    </Box>
  );
}

export default InvoiceTax;